/**
 * Dialog de config/ativação do PA: modo, valor, especialidade e horário só de hoje.
 * Horários usam TimeField (mesmo padrão FormTimeInput / FormDatePicker).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Button,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
} from "@mui/material";
import { LocalizationProvider, TimeField } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { type Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { toast } from "react-toastify";
import { useFluxPayContractContext } from "@/contexts/FluxPayContractContext";
import { usePractitionerRoleContext } from "@/contexts/PractitionerRoleContext";
import { formatBrlInput, parseBrlInput } from "./moneyUtils";
import { ProntoAtendimentoModeCards } from "./ProntoAtendimentoModeCards";
import { todayPtDayLongLabel } from "./buildProntoHealthcareServicePayload";
import {
  PRONTO_ATENDIMENTO_MODE_LABELS,
  type ProntoAtendimentoConfig,
  type ProntoAtendimentoConfigInput,
  type ProntoAtendimentoMode,
} from "./types";
import type { ProntoLocationOption } from "./locationHelpers";

interface ProntoAtendimentoConfigFlowProps {
  open: boolean;
  onClose: () => void;
  config: ProntoAtendimentoConfig | null;
  saving?: boolean;
  presencialLocations: ProntoLocationOption[];
  /** true quando aberto pelo switch ON — save ativa a agenda. */
  activateOnSave?: boolean;
  onSave: (input: ProntoAtendimentoConfigInput) => Promise<void>;
}

type FormState = {
  mode: ProntoAtendimentoMode;
  valueRaw: string;
  specialty: string;
  /** Uma única jornada (início → fim), sem divisão. */
  journeyStart: Dayjs | null;
  journeyEnd: Dayjs | null;
  locationChoice: "existing" | "new";
  selectedLocationId: string;
  newLocationName: string;
  newLocationCity: string;
  newLocationState: string;
};

const defaultJourneyStart = () => dayjs().second(0).millisecond(0);

/** Fim padrão: 18h se ainda for depois do início; senão fim do dia. */
const defaultJourneyEnd = (start: Dayjs) => {
  const eighteen = dayjs().hour(18).minute(0).second(0).millisecond(0);
  if (eighteen.isAfter(start)) return eighteen;
  return dayjs().hour(23).minute(59).second(0).millisecond(0);
};

function createBaseForm(): FormState {
  const journeyStart = defaultJourneyStart();
  return {
    mode: "presencial",
    valueRaw: "",
    specialty: "",
    journeyStart,
    journeyEnd: defaultJourneyEnd(journeyStart),
    locationChoice: "existing",
    selectedLocationId: "",
    newLocationName: "",
    newLocationCity: "",
    newLocationState: "",
  };
}

function toMinutes(value: Dayjs | null): number {
  if (!value || !value.isValid()) return NaN;
  return value.hour() * 60 + value.minute();
}

function toHhMm(value: Dayjs | null): string | null {
  if (!value || !value.isValid()) return null;
  return value.format("HH:mm");
}

/** TimeField no visual filled do FormTimeInput / FormDatePicker. */
function ProntoTimeField({
  label,
  value,
  onChange,
  minTime,
}: {
  label: string;
  value: Dayjs | null;
  onChange: (v: Dayjs | null) => void;
  /** Impede horários anteriores (ex.: início a partir de agora). */
  minTime?: Dayjs;
}) {
  return (
    <TimeField
      label={label}
      value={value}
      onChange={onChange}
      format="HH:mm"
      fullWidth
      minTime={minTime}
      sx={{
        flex: 1,
        "& .MuiFilledInput-root": { backgroundColor: "neutral100.main" },
      }}
      slotProps={{
        textField: {
          placeholder: "--:--",
          variant: "filled",
          size: "small",
        },
      }}
    />
  );
}

export function ProntoAtendimentoConfigFlow({
  open,
  onClose,
  config,
  saving = false,
  presencialLocations,
  activateOnSave = false,
  onSave,
}: ProntoAtendimentoConfigFlowProps) {
  const { userPractitionerRole } = usePractitionerRoleContext();
  const { hasFluxPayExtension } = useFluxPayContractContext();
  const [form, setForm] = useState<FormState>(() => createBaseForm());
  /** Limite inferior do início = momento em que o dialog abriu. */
  const [minStartTime, setMinStartTime] = useState<Dayjs>(() =>
    defaultJourneyStart(),
  );

  const specialtyOptions = useMemo(() => {
    const list =
      userPractitionerRole?.specialty
        ?.map((s) => {
          const coding = s.coding?.[0];
          const display = coding?.display || coding?.code || "";
          return display
            ? { value: display, label: display, code: coding?.code }
            : null;
        })
        .filter(Boolean) || [];
    // Remove duplicatas por display
    const seen = new Set<string>();
    return (list as Array<{ value: string; label: string; code?: string }>).filter(
      (o) => {
        if (seen.has(o.value)) return false;
        seen.add(o.value);
        return true;
      },
    );
  }, [userPractitionerRole?.specialty]);

  useEffect(() => {
    if (!open) {
      setForm(createBaseForm());
      return;
    }
    // Sempre recalcula início a partir da hora atual ao abrir
    const base = createBaseForm();
    setMinStartTime(base.journeyStart || defaultJourneyStart());

    const defaultSpecialty =
      config?.specialty ||
      config?.healthcareServiceDisplay ||
      specialtyOptions[0]?.value ||
      "";

    if (config) {
      setForm({
        ...base,
        mode: config.mode,
        valueRaw: formatBrlInput(config.value),
        specialty: defaultSpecialty,
        selectedLocationId: config.locationId || "",
        locationChoice:
          config.locationId || presencialLocations.length > 0
            ? "existing"
            : "new",
      });
      return;
    }
    setForm({
      ...base,
      specialty: specialtyOptions[0]?.value || "",
      selectedLocationId: presencialLocations[0]?.id || "",
      locationChoice: presencialLocations.length > 0 ? "existing" : "new",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, config?.id, specialtyOptions.length]);

  const handleSave = async () => {
    if (specialtyOptions.length === 0) {
      toast.info(
        "Configure sua especialidade no perfil profissional antes de ativar o Pronto Atendimento.",
      );
      return;
    }
    if (!form.specialty) {
      toast.info("Selecione a especialidade.");
      return;
    }

    // Valor só quando FluxPay está ativo (mesmo critério de ScheduleSettings)
    let value = 0;
    if (hasFluxPayExtension) {
      value = parseBrlInput(form.valueRaw);
      if (!Number.isFinite(value) || value < 5) {
        toast.info("Informe um valor válido (mínimo R$ 5,00).");
        return;
      }
    }

    const start = toMinutes(form.journeyStart);
    const end = toMinutes(form.journeyEnd);
    const startStr = toHhMm(form.journeyStart);
    const endStr = toHhMm(form.journeyEnd);
    if (
      !startStr ||
      !endStr ||
      !Number.isFinite(start) ||
      !Number.isFinite(end) ||
      end <= start
    ) {
      toast.info("Informe uma jornada válida (início antes do fim).");
      return;
    }

    // Início não pode ser anterior à hora atual
    const nowMinutes = toMinutes(dayjs());
    if (start < nowMinutes) {
      toast.info("O início da jornada deve ser a partir da hora atual.");
      return;
    }

    // PA: uma única jornada contínua (sem divisão em intervalos)
    const todayIntervals: ProntoAtendimentoConfigInput["todayIntervals"] = [
      { start: startStr, end: endStr },
    ];

    if (form.mode === "presencial") {
      if (form.locationChoice === "existing" && !form.selectedLocationId) {
        toast.info("Selecione uma localização presencial.");
        return;
      }
      if (form.locationChoice === "new" && !form.newLocationName.trim()) {
        toast.info("Informe o nome da nova localização.");
        return;
      }
    }

    const input: ProntoAtendimentoConfigInput = {
      mode: form.mode,
      value,
      specialty: form.specialty,
      todayIntervals,
      ...(activateOnSave && { activate: true }),
      ...(form.mode === "presencial" &&
        form.locationChoice === "existing" && {
          selectedLocationId: form.selectedLocationId,
        }),
      ...(form.mode === "presencial" &&
        form.locationChoice === "new" && {
          newLocation: {
            name: form.newLocationName.trim(),
            city: form.newLocationCity.trim() || undefined,
            state: form.newLocationState.trim() || undefined,
          },
        }),
    };

    try {
      await onSave(input);
      onClose();
    } catch {
      // toast no hook
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {activateOnSave
          ? "Ativar Pronto Atendimento"
          : "Configurar Pronto Atendimento"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
         

          {specialtyOptions.length === 0 ? (
            <Alert severity="warning">
              Nenhuma especialidade no seu perfil. Configure em Perfil
              profissional antes de continuar.
            </Alert>
          ) : (
            <TextField
              select
              variant="filled"
              size="small"
              fullWidth
              required
              label="Especialidade"
              value={form.specialty}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, specialty: e.target.value }))
              }
              helperText="Selecione a especialidade de pronto atendimento"
            >
              {specialtyOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* Uma jornada contínua só do dia atual — TimeField no padrão FormTimeInput */}
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
            <Stack
              spacing={1}
              sx={{
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2">
                Horário de atendimento — {todayPtDayLongLabel()}
              </Typography>
             
              <Stack direction="row" spacing={1} alignItems="center">
                <ProntoTimeField
                  label="Início"
                  value={form.journeyStart}
                  minTime={minStartTime}
                  onChange={(v) =>
                    setForm((prev) => ({ ...prev, journeyStart: v }))
                  }
                />
                <Typography variant="body2">até</Typography>
                <ProntoTimeField
                  label="Fim"
                  value={form.journeyEnd}
                  minTime={form.journeyStart || minStartTime}
                  onChange={(v) =>
                    setForm((prev) => ({ ...prev, journeyEnd: v }))
                  }
                />
              </Stack>
            </Stack>
          </LocalizationProvider>

          <Stack spacing={0.75}>
            <Typography variant="body2" fontWeight={500}>
              Modo de atendimento
            </Typography>
            <ProntoAtendimentoModeCards
              value={form.mode}
              onChange={(mode) =>
                setForm((prev) => ({
                  ...prev,
                  mode,
                  locationChoice:
                    mode === "presencial" && presencialLocations.length === 0
                      ? "new"
                      : prev.locationChoice,
                }))
              }
            />
          </Stack>

          {form.mode === "presencial" && (
            <Stack spacing={1}>
              <Typography variant="body2" fontWeight={500}>
                Localização presencial
              </Typography>
              <RadioGroup
                value={form.locationChoice}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    locationChoice: e.target.value as "existing" | "new",
                  }))
                }
              >
                <FormControlLabel
                  value="existing"
                  control={<Radio size="small" />}
                  disabled={presencialLocations.length === 0}
                  label="Usar localização existente"
                />
                <FormControlLabel
                  value="new"
                  control={<Radio size="small" />}
                  label="Criar nova localização"
                />
              </RadioGroup>

              {form.locationChoice === "existing" && (
                <TextField
                  select
                  variant="filled"
                  size="small"
                  fullWidth
                  label="Localização"
                  value={form.selectedLocationId}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      selectedLocationId: e.target.value,
                    }))
                  }
                >
                  {presencialLocations.map((loc) => (
                    <MenuItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {form.locationChoice === "new" && (
                <Stack spacing={1}>
                  <TextField
                    variant="filled"
                    size="small"
                    fullWidth
                    label="Nome do local"
                    value={form.newLocationName}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        newLocationName: e.target.value,
                      }))
                    }
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                      variant="filled"
                      size="small"
                      fullWidth
                      label="Cidade (opcional)"
                      value={form.newLocationCity}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          newLocationCity: e.target.value,
                        }))
                      }
                    />
                    <TextField
                      variant="filled"
                      size="small"
                      fullWidth
                      label="UF (opcional)"
                      value={form.newLocationState}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          newLocationState: e.target.value,
                        }))
                      }
                    />
                  </Stack>
                </Stack>
              )}
            </Stack>
          )}

         

          {hasFluxPayExtension && (
            <TextField
              variant="filled"
              size="small"
              fullWidth
              label="Valor da consulta"
              value={form.valueRaw}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, valueRaw: e.target.value }))
              }
              placeholder="0,00"
              helperText="Mínimo R$ 5,00"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">R$</InputAdornment>
                ),
              }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{ textTransform: "none" }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={saving || specialtyOptions.length === 0}
          sx={{ textTransform: "none" }}
        >
          {saving
            ? "Salvando..."
            : activateOnSave
              ? "Ativar"
              : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
